import equal from '../../node_modules/fast-deep-equal/index.js';
import { isUndefined, isNullOrUndefined, isEmptyArray } from '../guards/index.js';

const pick = (object, keys) => {
  return keys.reduce((acc, key) => {
    acc[key] = object[key];
    return acc;
  }, {});
};
const omit = (object, keys) => {
  const clone = {
    ...object
  };
  keys.forEach((key) => delete clone[key]);
  return clone;
};
const omitUndefined = (object) => {
  const clone = {
    ...object
  };
  Object.entries(clone).forEach(([key, value]) => {
    if (value === void 0) {
      delete clone[key];
    }
  });
  return clone;
};
const omitPropertiesBase = (object, shouldOmitFn) => {
  const clone = {
    ...object
  };
  Object.entries(clone).forEach(([key, value]) => {
    if (Array.isArray(shouldOmitFn) ? shouldOmitFn.some((fn) => fn(value)) : shouldOmitFn(value)) {
      delete clone[key];
    }
  });
  return clone;
};
const omitProperties = Object.assign(omitPropertiesBase, {
  isEmptyArray,
  isNullOrUndefined,
  isUndefined
});
function diffChange(newVal) {
  return (oldVal) => {
    const isEqual = equal(oldVal, newVal);
    return isEqual ? oldVal : newVal;
  };
}
function sanitizeForLogging(obj, options = {}) {
  const {
    maxStringLength = 5e3,
    maxArrayLength = 50,
    maxObjectKeys = 50,
    maxDepth = 15
  } = options;
  const seen = /* @__PURE__ */ new WeakSet();
  function safeGetProperty(obj2, key) {
    try {
      return obj2[key];
    } catch (error) {
      return `[Error accessing ${key}: ${error instanceof Error ? error.message : "Unknown error"}]`;
    }
  }
  function getObjectKeys(value) {
    try {
      const allKeys = Object.getOwnPropertyNames(value);
      return allKeys.filter((key) => {
        try {
          const descriptor = Object.getOwnPropertyDescriptor(value, key);
          if (descriptor && typeof descriptor.value === "function" && !descriptor.get) {
            return false;
          }
          return true;
        } catch {
          return true;
        }
      });
    } catch {
      return Object.keys(value);
    }
  }
  function handleSpecialObjects(value) {
    if (value instanceof Node) {
      const result = {
        nodeType: value.nodeType,
        nodeName: value.nodeName
      };
      switch (value.nodeType) {
        case Node.ELEMENT_NODE: {
          const element = value;
          result.tagName = element.tagName;
          if (element.id) result.id = element.id;
          if (element.className) result.className = element.className;
          const keyAttributes = ["data-testid", "data-id", "role", "type", "name", "href", "src", "alt", "title"];
          const attributes = {};
          let hasAttributes = false;
          for (const attr of keyAttributes) {
            const value2 = element.getAttribute(attr);
            if (value2 !== null) {
              attributes[attr] = value2;
              hasAttributes = true;
            }
          }
          if (hasAttributes) result.attributes = attributes;
          if (element instanceof HTMLElement && element.dataset) {
            const datasetKeys = Object.keys(element.dataset);
            if (datasetKeys.length > 0) {
              const dataset = {};
              const maxDatasetEntries = 10;
              for (let i = 0; i < Math.min(datasetKeys.length, maxDatasetEntries); i++) {
                const key = datasetKeys[i];
                dataset[key] = element.dataset[key] || "";
              }
              result.dataset = dataset;
              if (datasetKeys.length > maxDatasetEntries) {
                result.datasetTruncated = `... ${datasetKeys.length - maxDatasetEntries} more entries`;
              }
            }
          }
          const textContent = element.textContent?.trim();
          if (textContent && textContent.length > 0) {
            result.textContent = textContent.length > 100 ? textContent.slice(0, 100) + "..." : textContent;
          }
          result.childElementCount = element.childElementCount;
          if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
            result.value = element.value;
          }
          break;
        }
        case Node.TEXT_NODE: {
          const textNode = value;
          const content = textNode.textContent?.trim();
          if (content) {
            result.textContent = content.length > 200 ? content.slice(0, 200) + "..." : content;
          }
          break;
        }
        case Node.COMMENT_NODE: {
          const commentNode = value;
          const content = commentNode.nodeValue?.trim();
          if (content) {
            result.comment = content.length > 100 ? content.slice(0, 100) + "..." : content;
          }
          break;
        }
        case Node.DOCUMENT_NODE: {
          const doc = value;
          result.URL = doc.URL;
          result.title = doc.title;
          result.readyState = doc.readyState;
          break;
        }
        case Node.DOCUMENT_FRAGMENT_NODE: {
          const fragment = value;
          result.childElementCount = fragment.childElementCount;
          break;
        }
      }
      return result;
    }
    if (value === window || value && value.constructor && value.constructor.name === "Window") {
      const location = safeGetProperty(value, "location");
      return {
        type: "Window",
        location: location && typeof location === "object" ? safeGetProperty(location, "href") : null,
        innerWidth: safeGetProperty(value, "innerWidth"),
        innerHeight: safeGetProperty(value, "innerHeight")
      };
    }
    if (value && value.constructor && value.constructor.name === "Location") {
      return {
        type: "Location",
        href: safeGetProperty(value, "href"),
        pathname: safeGetProperty(value, "pathname"),
        search: safeGetProperty(value, "search"),
        hash: safeGetProperty(value, "hash")
      };
    }
    if (value && typeof value === "object") {
      if ("x" in value && "y" in value && "width" in value && "height" in value) {
        return {
          x: safeGetProperty(value, "x"),
          y: safeGetProperty(value, "y"),
          width: safeGetProperty(value, "width"),
          height: safeGetProperty(value, "height"),
          top: safeGetProperty(value, "top"),
          right: safeGetProperty(value, "right"),
          bottom: safeGetProperty(value, "bottom"),
          left: safeGetProperty(value, "left")
        };
      }
      if (value.constructor && value.constructor.name) {
        const constructorName = value.constructor.name;
        if (["DOMRect", "DOMRectReadOnly", "ClientRect"].includes(constructorName)) {
          return {
            x: safeGetProperty(value, "x"),
            y: safeGetProperty(value, "y"),
            width: safeGetProperty(value, "width"),
            height: safeGetProperty(value, "height"),
            top: safeGetProperty(value, "top"),
            right: safeGetProperty(value, "right"),
            bottom: safeGetProperty(value, "bottom"),
            left: safeGetProperty(value, "left")
          };
        }
        if (["DOMTokenList", "NamedNodeMap"].includes(constructorName)) {
          const result = {};
          try {
            const length = safeGetProperty(value, "length");
            if (typeof length === "number") {
              result.length = length;
              for (let i = 0; i < Math.min(length, maxArrayLength); i++) {
                result[i] = safeGetProperty(value, i.toString());
              }
            }
          } catch {
          }
          return Object.keys(result).length > 0 ? result : null;
        }
        if (constructorName.endsWith("Event")) {
          return {
            type: safeGetProperty(value, "type"),
            target: safeGetProperty(value, "target") ? handleSpecialObjects(safeGetProperty(value, "target")) : null,
            currentTarget: safeGetProperty(value, "currentTarget") ? handleSpecialObjects(safeGetProperty(value, "currentTarget")) : null,
            bubbles: safeGetProperty(value, "bubbles"),
            cancelable: safeGetProperty(value, "cancelable"),
            timeStamp: safeGetProperty(value, "timeStamp")
          };
        }
        if (constructorName === "CSSStyleDeclaration") {
          const result = {};
          const length = safeGetProperty(value, "length");
          if (typeof length === "number") {
            result.length = length;
            const maxStyles = 10;
            for (let i = 0; i < Math.min(length, maxStyles); i++) {
              const prop = safeGetProperty(value, i.toString());
              if (typeof prop === "string") {
                result[prop] = safeGetProperty(value, prop);
              }
            }
            if (length > maxStyles) {
              result["..."] = `[${length - maxStyles} more styles]`;
            }
          }
          return result;
        }
      }
    }
    return null;
  }
  function helper(value, depth) {
    if (depth > maxDepth) {
      return "[Max depth reached]";
    }
    if (value === null || typeof value !== "object") {
      if (typeof value === "string") {
        return value.length > maxStringLength ? value.slice(0, maxStringLength) + "... [truncated]" : value;
      }
      return value;
    }
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);
    if (Array.isArray(value)) {
      const length2 = Math.min(value.length, maxArrayLength);
      const result2 = value.slice(0, length2).map((item) => helper(item, depth + 1));
      if (value.length > length2) {
        result2.push(`... [${value.length - length2} more items]`);
      }
      return result2;
    }
    if (value instanceof Uint8Array) {
      return `[Uint8Array length=${value.length}]`;
    }
    const specialResult = handleSpecialObjects(value);
    if (specialResult) {
      const result2 = {};
      const keys2 = Object.keys(specialResult);
      const length2 = Math.min(keys2.length, maxObjectKeys);
      for (let i = 0; i < length2; i++) {
        const key = keys2[i];
        result2[key] = helper(specialResult[key], depth + 1);
      }
      if (keys2.length > length2) {
        result2["..."] = `[${keys2.length - length2} more keys]`;
      }
      return result2;
    }
    const keys = getObjectKeys(value);
    const length = Math.min(keys.length, maxObjectKeys);
    const result = {};
    for (let i = 0; i < length; i++) {
      const key = keys[i];
      const propertyValue = safeGetProperty(value, key);
      result[key] = helper(propertyValue, depth + 1);
    }
    if (keys.length > length) {
      result["..."] = `[${keys.length - length} more keys]`;
    }
    return result;
  }
  return helper(obj, 0);
}
function createObjectPreview(obj) {
  if (obj === null) return "null";
  if (obj === void 0) return "undefined";
  if (typeof obj !== "object") {
    return String(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => {
      if (item === null) return "null";
      if (item === void 0) return "undefined";
      if (typeof item === "object") {
        return Array.isArray(item) ? "[Array]" : "[Object]";
      }
      return String(item);
    }).join(" ");
  }
  return Object.entries(obj).map(([key, value]) => {
    let valueStr;
    if (value === null) {
      valueStr = "null";
    } else if (value === void 0) {
      valueStr = "undefined";
    } else if (typeof value === "object") {
      valueStr = Array.isArray(value) ? "[Array]" : "[Object]";
    } else {
      valueStr = String(value);
    }
    return `${key}: ${valueStr}`;
  }).join(" ");
}

export { createObjectPreview, diffChange, omit, omitProperties, omitUndefined, pick, sanitizeForLogging };
//# sourceMappingURL=manipulation.js.map
